import { Injectable, OnDestroy, inject } from '@angular/core';
import { PlatformService } from '../services/platform.service';

/**
 * Service that blurs the active input element when scrolling on elements with 'inner-scroll' class.
 * This helps dismiss the keyboard on mobile devices when the user scrolls.
 * 
 * Call `init()` once at app startup (e.g., in App component ngOnInit).
 */
@Injectable({
  providedIn: 'root'
})
export class BlurOnScrollService implements OnDestroy {
  private platform = inject(PlatformService);
  private scrollTimeout: any = null;
  private isScrolling = false;
  private initialized = false;
  private boundScrollHandler: (event: Event) => void;

  constructor() {
    this.boundScrollHandler = this.onScroll.bind(this);
  }

  /**
   * Initialize the global scroll listener for all elements with 'inner-scroll' class.
   * Should be called once at app startup.
   */
  init(): void {
    if (this.initialized) return;
    const doc = this.platform.document;
    if (!doc) return;
    
    // Use capturing phase to catch scroll events on any element
    doc.addEventListener('scroll', this.boundScrollHandler, { capture: true, passive: true });
    this.initialized = true;
    console.log('✅ BlurOnScrollService initialized');
  }

  ngOnDestroy(): void {
    const doc = this.platform.document;
    if (!doc) return;
    doc.removeEventListener('scroll', this.boundScrollHandler, { capture: true });
    if (this.scrollTimeout) {
      clearTimeout(this.scrollTimeout);
    }
  }

  private onScroll(event: Event): void {
    const target = event.target as HTMLElement;
    
    // Only handle scroll on elements with 'inner-scroll' class
    if (!target?.classList?.contains('inner-scroll')) {
      return;
    }

    // Only blur once at the start of scroll, not continuously
    if (!this.isScrolling) {
      this.isScrolling = true;
      this.blurActiveElement();
    }

    // Reset the scrolling flag after scroll ends
    if (this.scrollTimeout) {
      clearTimeout(this.scrollTimeout);
    }
    this.scrollTimeout = setTimeout(() => {
      this.isScrolling = false;
    }, 150);
  }

  private blurActiveElement(): void {
    const doc = this.platform.document;
    if (!doc) return;
    const activeElement = doc.activeElement as HTMLElement;
    
    // Check if the active element is an input-like element
    if (activeElement && this.isInputElement(activeElement)) {
      activeElement.blur();
    }
  }

  private isInputElement(element: HTMLElement): boolean {
    const tagName = element.tagName.toLowerCase();
    const inputTags = ['input', 'textarea', 'ion-input', 'ion-textarea'];
    
    // Check direct tag match
    if (inputTags.includes(tagName)) {
      return true;
    }

    // Check if it's inside an input component (like app-input)
    if (element.closest('app-input, app-textarea, ion-input, ion-textarea')) {
      return true;
    }

    // Check contenteditable
    if (element.getAttribute('contenteditable') === 'true') {
      return true;
    }

    return false;
  }
}
